import { Schema, SchemaSettings } from '@orbit/data'
import { KC } from '@/http/utils'

enum ModelTypes {
  DetailedPatient = 'detailed-patient',
}

enum ModelTypeNames {
  DetailedPatient = 'detailedPatient',
}

export interface CareGiver {
  id: string
  firstName: string
  lastName: string
}

export interface LungCapacity {
  timestamp?: number
  fev1?: number
  fvc1?: number
  fev1FvcRatio?: number
  fev1Predicted?: number
}

export interface HospitalOrErVisit {
  numberOfVisits?: number
  lastAdmission?: number
  admissionReason?: string
}

export interface Address {
  street?: string
  city?: string
  province?: string
  postalCode?: string
  country?: string
}

export interface SixMinutesWalk {
  distance?: number
  timestamp?: number
}

export interface MedicalInfo {
  hasCOPD?: boolean
  hasAsthma?: boolean
  otherConditions?: string
  sixMinutesWalks: SixMinutesWalk[]
  lungCapacities: LungCapacity[]
  erVisited?: boolean
  erVisit: HospitalOrErVisit[]
  onOxygenTherapy?: boolean
  activeSmoker?: boolean
  cigarettePerDay?: number
}

export interface Inhaler {
  brand?: string
  dosageRemaining?: number
  asOfDate?: number
  expiryDate?: number
  inhalerSn?: string
  inhalerNumber?: string
}

export interface Prescription {
  medication?: string
  usagePerDay?: number
  puffsPerUsage?: number
}

export interface PrescriptionsAndInhalers {
  prescriptions: Prescription[]
  inhalers: Inhaler[]
  brands: string[]
}

export interface PccPatient {
  patientId: string
  firstName?: string
  lastName?: string
  patientStatus?: string
  roomDesc?: string
  bedDesc?: string
  floorDesc?: string
  unitDesc?: string
}

export interface VibePatientItem {
  patientId?: string
  vibeId?: string
  gatewayId?: string
  trackerId?: string
  site?: string
  org?: string
  status?: string
}

export interface GenericDevice {
  type?: string
  deviceId?: string
  status?: string
  warning?: string
}

export interface VibePackage {
  vibeId?: string
  status?: string
  vibe?: VibePatientItem
  devices?: GenericDevice[]
  // tracker?: GenericDevice
  // inhaler?: GenericDevice
  // pulseOximeter?: GenericDevice
  // scale?: GenericDevice
  // thermometer?: GenericDevice
  // bpm?: GenericDevice
  // glucometer?: GenericDevice
}

export interface DetailedPatientAttributes {
  patientId: string
  careGivers: CareGiver[]
  notes: string[]
  hasDrugAllergy: boolean
  ethnicity: string
  timezone: string
  vibeAssigned: boolean
  drugAllergyInfo: string
  mainCareGiver: CareGiver
  otherMedications?: string
  medicalInfo?: MedicalInfo
  inhalerMedication: string[]
  otherCareGivers?: string
  email?: string
  lastName?: string
  address: Address
  maintenanceInhalers: PrescriptionsAndInhalers
  sex: string
  firstName: string
  phone?: string
  relieverInhalers?: PrescriptionsAndInhalers
  age?: number
  status: string // TODO define some type
  bmi?: number
  dateOfBirthIso8601?: string
  floorNumber?: string
  roomNumber?: string
  bedNumber?: string
  pccPatientId?: number
  sitePccPatients?: PccPatient[]
  vibePackage?: VibePackage
  availableVibes?: VibePatientItem[]
  updatePackage?: boolean
  removePackage?: boolean
}

export interface DetailedPatientSchema {
  id: string
  attributes: DetailedPatientAttributes
}

const schemaDefinition: SchemaSettings = {
  models: {
    [ModelTypeNames.DetailedPatient]: {
      attributes: {
        patientId: { type: 'string' },
        careGivers: { type: 'array' },
        notes: { type: 'array' },
        hasDrugAllergy: { type: 'string' },
        ethnicity: { type: 'string' },
        timezone: { type: 'string' },
        vibeAssigned: { type: 'string' },
        drugAllergyInfo: { type: 'string' },
        mainCareGiver: { type: 'object' },
        otherMedications: { type: 'string' },
        medicalInfo: { type: 'object' },
        inhalerMedication: { type: 'array' },
        otherCareGivers: { type: 'string' },
        email: { type: 'string' },
        lastName: { type: 'string' },
        address: { type: 'string' },
        maintenanceInhalers: { type: 'object' },
        sex: { type: 'string' },
        firstName: { type: 'string' },
        phone: { type: 'string' },
        relieverInhalers: { type: 'object' },
        age: { type: 'number' },
        status: { type: 'string' },
        bmi: { type: 'number' },
        dateOfBirthIso8601: { type: 'string' },
        floorNumber: { type: 'string' },
        roomNumber: { type: 'string' },
        bedNumber: { type: 'string' },
        pccPatientId: { type: 'number' },
        sitePccPatients: { type: 'array' },
        vibePackage: { type: 'object' },
        availableVibes: { type: 'array' },
        updatePackage: { type: 'boolean' },
        removePackage: { type: 'boolean' },
      },
    },
  },
}

export async function getDetailedPatientByIdQuery(id: string): Promise<DetailedPatientSchema[]> {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  console.log('Before calling find record for detailed patient', id)
  return remote
    .query(q => q.findRecords(ModelTypes.DetailedPatient).filter({ attribute: 'id', value: id }))
    .catch(() => console.log('fetch failed in get detailed patient ', id))
}

export async function updatePatientQuery(patient: DetailedPatientAttributes & { id: string }) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote.push(q => {
    if (patient.vibePackage) {
      patient.vibePackage.vibe = null
    }
    return q.updateRecord({
      type: ModelTypeNames.DetailedPatient,
      id: patient.id,
      attributes: {
        lastName: patient.lastName,
        firstName: patient.firstName,
        notes: patient.notes,
        address: patient.address,
        phone: patient.phone,
        email: patient.email,
        inhalerMedication: patient.inhalerMedication,
        otherMedications: patient.otherMedications,
        hasDrugAllergy: patient.hasDrugAllergy,
        drugAllergyInfo: patient.drugAllergyInfo,
        age: patient.age,
        sex: patient.sex,
        ethnicity: patient.ethnicity,
        bmi: patient.bmi,
        medicalInfo: patient.medicalInfo,
        maintenanceInhalers: patient.maintenanceInhalers,
        relieverInhalers: patient.relieverInhalers,
        mainCareGiver: patient.mainCareGiver,
        otherCareGivers: patient.otherCareGivers,
        floorNumber: patient.floorNumber,
        roomNumber: patient.roomNumber,
        bedNumber: patient.bedNumber,
        pccPatientId: patient.pccPatientId || null,
        vibePackage: patient.vibePackage || null,
        updatePackage: patient.updatePackage || false,
        removePackage: patient.removePackage || false,
      },
    })
  })
}
